import React, { useState, useRef, useCallback } from 'react';
import { X, Image as ImageIcon, Upload, Loader2, Wrench, Calendar, Type, AlignLeft, Hash, Gauge, User } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Modal } from '../common';
import toast from 'react-hot-toast';

const todayStr = () => new Date().toISOString().slice(0, 10);

const TIPOS_MTTO = ['CORRECTIVO', 'PREVENTIVO', 'PREDICTIVO', 'INSPECCION'];
const ESTATUS_OPTIONS = ['PENDIENTE', 'EN PROCESO', 'EJECUTADO'];

const CreateReporteModal = ({ isOpen, onClose, onSuccess }) => {
  const [fecha, setFecha] = useState(todayStr());
  const [equipo, setEquipo] = useState('');
  const [tipoMtto, setTipoMtto] = useState('CORRECTIVO');
  const [parteFalla, setParteFalla] = useState('');
  const [detalle, setDetalle] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [horometro, setHorometro] = useState('');
  const [estatus, setEstatus] = useState('PENDIENTE');
  const [realizadoPor, setRealizadoPor] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const imageInputRef = useRef(null);

  const resetForm = () => {
    setFecha(todayStr());
    setEquipo('');
    setTipoMtto('CORRECTIVO');
    setParteFalla('');
    setDetalle('');
    setCantidad(1);
    setHorometro('');
    setEstatus('PENDIENTE');
    setRealizadoPor('');
    setImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setError(null);
  };

  const handleImageSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError(null);
    } else if (file) {
      setError('Formato no soportado. Usa imágenes (PNG, JPG, GIF, WEBP).');
    }
  };

  const removeImage = () => {
    setImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleImageSelect(e.dataTransfer.files[0]);
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fecha) {
      setError('La fecha es obligatoria');
      return;
    }
    if (!equipo.trim()) {
      setError('El equipo es obligatorio');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('fecha', fecha);
    formData.append('equipo', equipo.trim());
    formData.append('tipo_mtto', tipoMtto);
    formData.append('parte_falla', parteFalla.trim());
    formData.append('detalle', detalle.trim());
    formData.append('cantidad', cantidad || 1);
    if (horometro !== '') formData.append('horometro', horometro);
    formData.append('estatus', estatus);
    formData.append('realizado_por', realizadoPor.trim());
    if (image) formData.append('image', image);

    try {
      await axiosInstance.post('/api/equipos-moviles/reportes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Reporte agregado');
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creando reporte:', err);
      setError(err.response?.data?.error || 'Error al guardar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all";
  const labelClass = "flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header onClose={handleClose}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-sura-blue to-sura-blue-light rounded-xl text-white shadow-lg shadow-sura-blue/20">
            <Wrench size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Nuevo Reporte de Equipo Móvil</h3>
            <p className="text-xs text-gray-500 font-normal">Servicios Generales</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} id="create-reporte-form" className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}><Calendar size={13} /> Fecha</label>
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}><Type size={13} /> Equipo</label>
              <input
                type="text"
                value={equipo}
                onChange={(e) => setEquipo(e.target.value)}
                placeholder="Ej: Montacarga 7, Camión JAC..."
                className={inputClass}
                maxLength={150}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Tipo de Mantenimiento</label>
              <select value={tipoMtto} onChange={(e) => setTipoMtto(e.target.value)} className={inputClass}>
                {TIPOS_MTTO.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Estatus</label>
              <select value={estatus} onChange={(e) => setEstatus(e.target.value)} className={inputClass}>
                {ESTATUS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Parte de la Falla</label>
            <input
              type="text"
              value={parteFalla}
              onChange={(e) => setParteFalla(e.target.value)}
              placeholder="Ej: Dosificador de gas, Tren delantero..."
              className={inputClass}
              maxLength={200}
            />
          </div>

          <div>
            <label className={labelClass}><AlignLeft size={13} /> Detalle del Correctivo</label>
            <textarea
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Qué se realizó..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}><Hash size={13} /> Cantidad</label>
              <input type="number" min="1" value={cantidad} onChange={(e) => setCantidad(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}><Gauge size={13} /> Horómetro</label>
              <input type="number" step="0.1" value={horometro} onChange={(e) => setHorometro(e.target.value)} placeholder="Opcional" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}><User size={13} /> Realizado Por</label>
              <input type="text" value={realizadoPor} onChange={(e) => setRealizadoPor(e.target.value)} placeholder="Opcional" className={inputClass} />
            </div>
          </div>

          <div>
            <label className={labelClass}><ImageIcon size={13} /> Imagen (opcional)</label>

            {!imagePreview && (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-5 transition-all duration-200 ${
                  dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className={`p-2.5 rounded-full transition-colors ${dragActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    <Upload size={18} className={dragActive ? 'text-blue-600' : 'text-gray-400'} />
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                    >
                      <ImageIcon size={14} className="text-blue-600" />
                      Seleccionar imagen
                    </button>
                  </div>
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                  onChange={(e) => handleImageSelect(e.target.files[0])}
                  className="hidden"
                />
              </div>
            )}

            {imagePreview && (
              <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-900/95 flex items-center justify-center">
                <img src={imagePreview} alt="Preview" className="w-auto h-auto max-h-[240px] max-w-full object-contain" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-md text-gray-600 hover:text-red-500 transition-all"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-fadeIn">
              <X size={16} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-end w-full gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="create-reporte-form"
            disabled={loading}
            className="px-6 py-2.5 font-semibold rounded-xl transition-all flex items-center gap-2 shadow-sm bg-gradient-to-r from-sura-blue to-sura-blue-light text-white hover:from-sura-blue-dark hover:to-sura-blue shadow-sura-blue/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Guardando...' : 'Guardar Reporte'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateReporteModal;
